import React, { useState } from 'react';
import laboratoryInfo from '../config';
import { useData } from '../contexts/DataContext';
import { getLotNumbers } from '../data/materialLotData';
import DocumentPreview from './DocumentPreview';
import { FaFileInvoice, FaPrint } from 'react-icons/fa';

const InvoiceGenerator = ({ order, invoice, showButton = true, buttonLabel = "Facturer", isExisting = false, className }) => {
    const { products, clients, addInvoice, updateOrder, invoices } = useData();
    const [showPreview, setShowPreview] = useState(false);

    const currentOrder = order || {};
    const currentInvoice = invoice || {};
    const practitionerClient = clients ? clients.find(c => c.id === (currentOrder?.clientId || currentOrder?.client_id)) : null;

    const items = currentInvoice.items || [];
    let total = currentInvoice.amount || 0;

    const currentId = currentOrder.id || 'preview';

    // Determine invoice number (correlated with order caseNumber)
    let generatedInvoiceNumber = currentInvoice.invoiceNumber || currentInvoice.invoice_number;

    if (!generatedInvoiceNumber && currentInvoice.id && String(currentInvoice.id).startsWith('ENO')) {
        generatedInvoiceNumber = currentInvoice.id;
    }

    if (!generatedInvoiceNumber && currentOrder.id) {
        const existingInv = (invoices || []).find(inv => 
            (inv.orderId && inv.orderId === currentOrder.id) || 
            (inv.order_id && inv.order_id === currentOrder.id) || 
            (currentOrder.invoiceId && inv.id === currentOrder.invoiceId)
        );
        if (existingInv) {
            generatedInvoiceNumber = existingInv.invoiceNumber || existingInv.invoice_number || existingInv.id;
        }
    }

    if (!generatedInvoiceNumber) {
        generatedInvoiceNumber = currentOrder.caseNumber || (typeof currentId === 'string' ? currentId.substring(0, 8).toUpperCase() : 'PREVIEW');
    }

    const fileName = `facture-${generatedInvoiceNumber}`;
    const targetId = `invoice-img-${generatedInvoiceNumber}`;

    if (!currentInvoice.items && currentOrder.teethConfig) {
        if (Array.isArray(currentOrder.teethConfig)) {
            // New Array Format
            currentOrder.teethConfig.forEach(line => {
                const { productId, teeth, details } = line;
                const product = products.find(p => p.id === productId);

                if (product) {
                    let priceKey = 'priceClassique';
                    if (currentOrder.gamme === 'paris') priceKey = 'priceParis';
                    if (currentOrder.gamme === 'eco') priceKey = 'priceEco';

                    const unitPrice = product[priceKey];
                    const quantity = line.quantity !== undefined && line.quantity !== null && !isNaN(Number(line.quantity)) 
                        ? Number(line.quantity) 
                        : (teeth.length || 1);
                    const itemPrice = unitPrice * quantity;
                    const toothLabel = teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', ');
                    let description = `${product.name}`;

                    if (details && details.teinte) {
                        description += `, Teinte ${details.teinte}`;
                    }

                    items.push({ toothId: toothLabel, description, quantity, unitPrice, price: itemPrice });
                    total += itemPrice;
                }
            });
        } else {
            // Legacy Object Format
            Object.entries(currentOrder.teethConfig).forEach(([toothId, config]) => {
                const { productId, details } = config;
                const product = products.find(p => p.id === productId);

                if (product) {
                    let priceKey = 'priceClassique';
                    if (currentOrder.gamme === 'paris') priceKey = 'priceParis';
                    if (currentOrder.gamme === 'eco') priceKey = 'priceEco';

                    let unitPrice = product[priceKey];
                    let itemPrice = unitPrice;
                    let description = `${product.name}`;

                    if (details && details.teinte) {
                        description += `, Teinte ${details.teinte}`;
                    }

                    items.push({ toothId, description, quantity: 1, unitPrice, price: itemPrice });
                    total += itemPrice;
                }
            });
        }
    }

    const handleBeforeGenerate = async () => {
        // Save record ONLY if it's a NEW invoice
        if (!isExisting && currentOrder.id && currentOrder.id !== 'preview') {
            try {
                const newInvoice = {
                    invoiceNumber: generatedInvoiceNumber,
                    orderId: currentOrder.id,
                    clientId: currentOrder.clientId || null,
                    patientName: currentOrder.patientName,
                    practitionerName: currentOrder.practitionerName,
                    items: items,
                    amount: total,
                    date: new Date().toISOString(),
                    status: 'Emise'
                };
                // addInvoice returns the created invoice with its actual UUID
                const createdInvoice = await addInvoice(newInvoice);

                // Update order status - use the actual UUID from the created invoice
                await updateOrder(currentOrder.id, {
                    status: 'Facturé',
                    invoiceId: createdInvoice?.id || null,
                    invoicedAt: new Date().toISOString()
                });
            } catch (error) {
                console.error("Erreur lors de la création de la facture:", error);
                alert("Erreur lors de l'enregistrement de la facture. La facture a été générée mais non sauvegardée.");
            }
        }
    };

    return (
        <>
            {showButton && (
                <button
                    className={className || `btn btn-sm ${isExisting ? 'btn-outline-primary' : 'btn-outline-success'}`}
                    onClick={(e) => { e.stopPropagation(); setShowPreview(true); }}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                    {isExisting ? <FaPrint size={13} /> : <FaFileInvoice size={13} />}
                    <span>{isExisting ? "Voir / Imprimer" : buttonLabel}</span>
                </button>
            )}

            {showPreview && (
                <DocumentPreview
                    targetId={targetId}
                    fileName={fileName}
                    onClose={() => setShowPreview(false)}
                    onAfterGenerate={handleBeforeGenerate}
                />
            )}

            {/* Hidden Template for Image Capture */}
            <div id={targetId} style={{ display: 'none' }}>
                <div style={{ backgroundColor: 'white', padding: '20mm', color: 'black', width: '210mm', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px', borderBottom: '2px solid #333', paddingBottom: '20px' }}>
                        <div>
                            <h2 style={{ margin: '0 0 10px 0', color: '#1e3a8a' }}>{laboratoryInfo.name}</h2>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>{laboratoryInfo.address}</p>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>Tel: {laboratoryInfo.phone}</p>
                            <p style={{ margin: '2px 0', fontSize: '13px' }}>{laboratoryInfo.email}</p>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <h1 style={{ margin: '0 0 10px 0', color: '#1e3a8a', fontSize: '28px' }}>FACTURE</h1>
                            <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '16px' }}>N° {generatedInvoiceNumber}</p>
                            <p style={{ margin: '2px 0', fontSize: '14px' }}>Date: {new Date(currentInvoice.date || new Date()).toLocaleDateString()}</p>
                        </div>
                    </div>

                    <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ width: '50%', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '8px', backgroundColor: '#f8fafc' }}>
                            <h3 style={{ marginTop: '0', fontSize: '14px', color: '#64748b', textTransform: 'uppercase', marginBottom: '10px' }}>Facturé à:</h3>
                            <p style={{ fontWeight: 'bold', margin: '5px 0', fontSize: '16px', color: '#1e293b' }}>Docteur {currentOrder.practitionerName || currentInvoice.practitionerName}</p>
                            {practitionerClient?.address && <p style={{ margin: '5px 0', fontSize: '13px', color: '#475569' }}>{practitionerClient.address}</p>}
                            <p style={{ margin: '5px 0', fontSize: '14px' }}>Patient: {currentOrder.patientName || currentInvoice.patientName}</p>
                            {(currentOrder.patientReference || currentOrder.patient_reference || currentInvoice.patientReference || currentInvoice.patient_reference) && (
                                <p style={{ margin: '5px 0', fontSize: '14px' }}>Référence : <strong>{currentOrder.patientReference || currentOrder.patient_reference || currentInvoice.patientReference || currentInvoice.patient_reference}</strong></p>
                            )}
                        </div>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '40px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#1e3a8a', color: 'white' }}>
                                <th style={{ textAlign: 'left', padding: '14px' }}>Désignation des travaux</th>
                                <th style={{ textAlign: 'center', padding: '14px 4px', width: '140px' }}>Dent</th>
                                <th style={{ textAlign: 'center', padding: '14px', width: '70px' }}>Quantité</th>
                                <th style={{ textAlign: 'right', padding: '14px', width: '90px' }}>P.U. HT</th>
                                <th style={{ textAlign: 'right', padding: '14px', width: '100px' }}>Montant HT</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => {
                                const qty = item.quantity || 1;
                                const unitPrice = item.unitPrice || (item.price / qty);
                                return (
                                    <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                        <td style={{ padding: '14px', fontSize: '14px' }}>{item.description}</td>
                                        <td style={{ textAlign: 'center', padding: '14px 4px', fontSize: '13px', fontWeight: 'bold', lineHeight: '1.4' }}>{item.toothId}</td>
                                        <td style={{ textAlign: 'center', padding: '14px', fontSize: '14px' }}>{qty}</td>
                                        <td style={{ textAlign: 'right', padding: '14px', fontSize: '14px' }}>{Number(unitPrice).toFixed(2)} €</td>
                                        <td style={{ textAlign: 'right', padding: '14px', fontSize: '14px', fontWeight: 'bold' }}>{item.price.toFixed(2)} €</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <div style={{ width: '300px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontSize: '14px' }}>
                                <span style={{ color: '#64748b' }}>Total HT:</span>
                                <span style={{ fontWeight: 600 }}>{total.toFixed(2)} €</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 0', borderTop: '2px solid #1e3a8a', marginTop: '8px', fontSize: '20px', fontWeight: 'bold', color: '#1e3a8a' }}>
                                <span>NET À PAYER:</span>
                                <span>{total.toFixed(2)} €</span>
                            </div>
                        </div>
                    </div>

                    {/* Notes & Remarques Section */}
                    {(currentOrder.notes || currentInvoice.notes) && (currentOrder.notes?.trim() || currentInvoice.notes?.trim()) && (
                        <div style={{ marginTop: '20px', backgroundColor: '#f8fafc', padding: '12px 15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#1e3a8a', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px' }}>NOTES & REMARQUES</h4>
                            <div style={{ fontSize: '12px', color: '#334155', whiteSpace: 'pre-wrap' }}>
                                <strong>Remarque générale :</strong> {currentOrder.notes || currentInvoice.notes}
                            </div>
                        </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '30px', marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #e2e8f0', fontSize: '12px' }}>
                        <div>
                            <h4 style={{ margin: '0 0 8px 0', color: '#1e3a8a', fontSize: '13px' }}>DÉCLARATION DE CONFORMITÉ & TRAÇABILITÉ</h4>
                            <p style={{ margin: '3px 0', fontSize: '11px', color: '#475569', lineHeight: '1.4' }}>
                                <strong>Dispositif Médical Sur Mesure (DMSM)</strong> conforme au Règlement Européen (UE) 2017/745.<br />
                                Ce dispositif a été fabriqué pour l'usage exclusif du patient mentionné ci-dessus et répond aux exigences générales de sécurité et de performances.
                            </p>
                            <div style={{ marginTop: '10px', backgroundColor: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px dashed #cbd5e1' }}>
                                <span style={{ fontSize: '11px', color: '#64748b' }}><strong>Traçabilité des composants / N° de Lots :</strong></span>
                                <div style={{ fontSize: '11px', color: '#334155', marginTop: '4px', lineHeight: '1.8' }}>
                                     {(() => {
                                         const allLots = new Map();
                                         
                                         // 1. Process items from invoice
                                         items.forEach(item => {
                                             let teinte = item.details?.teinte;
                                             if (!teinte && item.description) {
                                                 const match = item.description.match(/Teinte\s+([A-Za-z0-9.]+)/i);
                                                 if (match) teinte = match[1];
                                             }
                                             const lots = getLotNumbers(item.category, item.description || item.name, teinte, item.notes);
                                             lots.forEach(l => { if (!allLots.has(l.label)) allLots.set(l.label, l.value); });
                                         });

                                         // 2. Also process currentOrder teethConfig if available
                                         if (currentOrder?.teethConfig && Array.isArray(currentOrder.teethConfig)) {
                                             currentOrder.teethConfig.forEach(line => {
                                                 const product = products.find(p => p.id === line.productId);
                                                 if (product) {
                                                     const lots = getLotNumbers(product.category, product.name, line.details?.teinte, line.notes);
                                                     lots.forEach(l => { if (!allLots.has(l.label)) allLots.set(l.label, l.value); });
                                                 }
                                             });
                                         }

                                         if (allLots.size > 0) {
                                             return Array.from(allLots.entries()).map(([label, value], idx) => (
                                                 <div key={idx}><strong>{label} :</strong> {value}</div>
                                             ));
                                         }
                                         return 'Matériaux d\'implant / céramique / usinage : Lot ____________________';
                                     })()}
                                </div>
                            </div>
                        </div>
                        <div style={{ border: '1px solid #cbd5e1', borderRadius: '8px', padding: '10px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: '100px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#475569' }}>Visa & Contrôle Qualité Labo</span>
                            <div style={{ textAlign: 'center', margin: '5px 0', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <img src="/signature.png" alt="Signature" style={{ height: '50px', maxHeight: '50px', objectFit: 'contain' }} />
                            </div>
                            <div style={{ borderTop: '1px dashed #cbd5e1', paddingTop: '5px', fontSize: '10px', color: '#94a3b8' }}>
                                Date de libération & Signature
                            </div>
                        </div>
                    </div>

                    <div style={{
                        marginTop: '30px',
                        textAlign: 'center',
                        fontSize: '10px',
                        color: '#94a3b8',
                        borderTop: '1px solid #f1f5f9',
                        paddingTop: '10px'
                    }}>
                        <p style={{ fontWeight: 'bold', margin: '0 0 4px 0' }}>{laboratoryInfo.fullName} - SIRET: {laboratoryInfo.siret} - TVA: {laboratoryInfo.tva}</p>
                        <p style={{ margin: '2px 0', fontStyle: 'italic' }}>Système de management de la qualité conforme aux exigences de la norme ISO 13485 (Dispositifs Médicaux)</p>
                        <p style={{ margin: '4px 0 0 0', fontSize: '9px' }}>Pénalités de retard : 3 fois le taux légal. Indemnité forfaitaire de recouvrement en cas de retard : 40 €.</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default InvoiceGenerator;
